/**
 * BILLETTERIE (LC Porte) — modèle pur de la fiche Intégrations › Billetterie
 * (testable sans navigateur). Le serveur décide de tout ; ici, on dit en
 * français ce qu'il a répondu.
 */

export interface ConnexionBilletterie {
  configured: boolean
  api_url: string | null
  token: { set: boolean; updated_at: string | null }
  status: { reachable: boolean; error_code: string | null; message: string | null }
  copie: { acces: number; derniere_lecture: string | null }
}

/** Au-delà, la copie des droits par la billetterie est jugée arrêtée (elle lit chaque minute). */
export const COPIE_ARRETEE_MS = 10 * 60_000

/** La pastille du raccordement : « Jeton refusé » n'est pas « Injoignable ». */
export function pastilleLiaison(c: ConnexionBilletterie | null): { ton: 'ok' | 'warning' | 'error' | 'idle'; texte: string } {
  if (!c) return { ton: 'idle', texte: '—' }
  if (!c.configured) return { ton: 'idle', texte: 'Non raccordée' }
  const code = c.status.error_code
  if (code === 'BILLETTERIE_TOKEN_REFUSED') return { ton: 'error', texte: 'Jeton refusé' }
  if (code === 'BILLETTERIE_AUTRE_SERVICE') return { ton: 'error', texte: 'Jeton d’un autre service' }
  if (code === 'BILLETTERIE_DISABLED') return { ton: 'idle', texte: 'Coupée (Mode sûr)' }
  if (c.status.reachable) return { ton: 'ok', texte: 'Raccordée' }
  return { ton: 'warning', texte: 'Injoignable' }
}

/** Ce que la billetterie fait de la liste des personnes, en une phrase. */
export function phraseCopie(c: ConnexionBilletterie, maintenant: number = Date.now()): { ton: 'ok' | 'warning' | 'idle'; texte: string } {
  if (c.copie.acces === 0) {
    return { ton: 'warning', texte: 'Aucun accès de service « billetterie.read » : la billetterie ne peut pas copier les droits de porte. Créez-en un dans Accès de service, puis rangez-le sur le serveur de la billetterie.' }
  }
  if (!c.copie.derniere_lecture) return { ton: 'idle', texte: 'La billetterie n’a pas encore lu la liste des personnes.' }
  const age = maintenant - Date.parse(c.copie.derniere_lecture)
  if (age > COPIE_ARRETEE_MS) return { ton: 'warning', texte: `La billetterie n’a plus lu la liste des personnes depuis ${quandParis(c.copie.derniere_lecture)} : ses droits de porte ne suivent plus Gestion.` }
  return { ton: 'ok', texte: `Droits de porte copiés par la billetterie ${quandParis(c.copie.derniere_lecture)}.` }
}

export function validerAdresse(v: string): string | null {
  if (!v) return 'L’adresse de la billetterie est requise (par exemple http://billetterie-club:4192).'
  try {
    const u = new URL(v)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'L’adresse doit commencer par http:// ou https://.'
    if (u.username || u.password || u.search || u.hash) return 'L’adresse ne doit contenir ni identifiant, ni requête, ni fragment.'
  } catch { return 'Adresse invalide (par exemple http://billetterie-club:4192).' }
  return null
}

/** Un jeton de service de la billetterie : `bsk_` suivi de 40 à 60 caractères. */
export const JETON_BILLETTERIE_RX = /^bsk_[A-Za-z0-9_-]{40,60}$/

const HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
const HEURE_SEULE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
const JOUR = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' })

/** « à 23:41 » aujourd'hui (heure de Paris), sinon « le 22 octobre à 23:41 ». */
export function quandParis(iso: string | null, maintenant: number = Date.now()): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  return JOUR.format(t) === JOUR.format(maintenant) ? `à ${HEURE_SEULE.format(t)}` : `le ${HEURE.format(t)}`
}

/** Le message d'une réponse en erreur (`{ error, message }`), sinon un repli honnête. */
export function messageErreur(status: number, data: unknown, repli: string): string {
  const m = typeof data === 'object' && data !== null ? (data as { message?: unknown }).message : null
  if (typeof m === 'string' && m.trim()) return m
  if (status === 403) return 'Vous n’avez pas accès à cette fonction.'
  if (status === 0) return 'Gestion n’a pas répondu.'
  return repli
}
